import React, { useState } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, Typography, makeStyles } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Icon } from "./ds";
import tokens from "./designTokens";
import AgentDrawer from "./AgentDrawer";
import QualityTab from "./quality/QualityTab";
import ReviewQueue from "./quality/ReviewQueue";
import EvalRunsTab from "./quality/EvalRunsTab";
import LoopTab from "./quality/LoopTab";
import { RangeChips } from "./FilterBar";
import { rangeForPreset, istToday, istYesterday } from "./fleetRange";
import { useTableStyles } from "./tableStyles";

const VIEWS = [
  { key: "quality", label: "Quality", icon: "check" },
  { key: "loop", label: "Loop", icon: "refresh-cw" },
  { key: "review", label: "Review queue", icon: "list" },
  { key: "evals", label: "Eval runs", icon: "trending-up" },
];

const useStyles = makeStyles({
  // The superadmin layout hands routes a fixed clipped box — this page owns
  // its scroll.
  root: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    overflowY: "auto",
    padding: "20px 24px 48px",
    background: "#F7F8FA",
  },
  content: { maxWidth: 1720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 },
  title: { fontFamily: tokens.fontHeading, fontWeight: 700, fontSize: 21, letterSpacing: "-.3px", color: "#232D2F" },
  subtitle: { fontFamily: tokens.fontBody, fontSize: 13, color: tokens.textSecondary, marginTop: 2 },
  refreshBtn: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 10,
    background: tokens.surface,
    color: tokens.primary,
    width: 38,
    height: 38,
    "&:hover": { background: tokens.primaryLight },
  },
  viewChip: {
    background: tokens.surface,
    color: tokens.textSecondary,
    border: `1px solid ${tokens.border}`,
    fontFamily: tokens.fontBody,
    fontSize: 12,
    fontWeight: 600,
  },
  viewChipOn: {
    background: tokens.primaryLight,
    color: tokens.primary,
    border: "1px solid #B9D6D1",
    fontWeight: 700,
    "&:hover, &:focus": { background: tokens.primaryLight },
  },
  panel: { padding: 16, background: "#FAFBFC" },
});

export default function AgentFleetPage() {
  const classes = useStyles();
  const table = useTableStyles();
  const [view, setView] = useState("quality");
  const [preset, setPreset] = useState("7d");
  const [customDate, setCustomDate] = useState(() => istYesterday());
  const [refreshKey, setRefreshKey] = useState(0);
  const [qualityBot, setQualityBot] = useState(null);

  const range = rangeForPreset(preset, customDate, istToday());
  const activeView = VIEWS.find((v) => v.key === view) || VIEWS[0];

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Box className={classes.header}>
          <Box>
            <Typography component="div" className={classes.title}>
              Agent Analytics
            </Typography>
            <Typography component="div" className={classes.subtitle}>
              Judge quality, the dream loop, human review and eval runs — pick an agent to open its episodes and dreams.
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton data-testid="refresh-btn" className={classes.refreshBtn} onClick={() => setRefreshKey((k) => k + 1)}>
              <RefreshIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Paper elevation={0} className={table.card}>
          <Box className={table.cardHeader}>
            <Typography component="div" className={table.cardTitle}>
              <Icon name={activeView.icon} size={17} style={{ color: tokens.primary }} />
              {activeView.label}
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
              <Box display="flex" gridGap={6}>
                {VIEWS.map((v) => {
                  const on = v.key === view;
                  return (
                    <Chip
                      key={v.key}
                      size="small"
                      clickable
                      data-testid={`view-chip-${v.key}`}
                      label={v.label}
                      onClick={() => setView(v.key)}
                      className={`${classes.viewChip} ${on ? classes.viewChipOn : ""}`}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>

          {view === "quality" && (
            <Box className={classes.panel}>
              <Box display="flex" alignItems="center" gridGap={8} mb={1.75}>
                <RangeChips preset={preset} onPreset={setPreset} customDate={customDate} onCustomDate={setCustomDate} />
              </Box>
              <QualityTab
                range={range}
                refreshKey={refreshKey}
                onOpenAgent={(a) => setQualityBot({ project_id: a.project_id, agent_id: a.agent_id, flow_id: "main" })}
              />
            </Box>
          )}
          {view === "loop" && (
            <Box className={classes.panel}>
              <LoopTab refreshKey={refreshKey} />
            </Box>
          )}
          {view === "review" && <ReviewQueue refreshKey={refreshKey} />}
          {view === "evals" && (
            <Box className={classes.panel}>
              <EvalRunsTab refreshKey={refreshKey} />
            </Box>
          )}
        </Paper>
      </div>
      <AgentDrawer bot={qualityBot} range={range} onClose={() => setQualityBot(null)} />
    </div>
  );
}
